import React from 'react';

import { StopCircleIcon, PlayCircleIcon, PauseCircleIcon } from '@vlabs/icons';
import { TruncatedText } from '@vlabs/shared/components/truncated-text/TruncatedText';
import { ButtonScrimGroup, Control } from '@vlabs/uikit';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import st from './SharedCell.module.sass';

export function NotificationStatus({
  value: {
    notification = {},
    pausedNotification = undefined,
    notificationId = undefined,
    isEnable = undefined,
    onClick = undefined,
    onPause = undefined,
  },
}) {
  const { t } = useTranslation();

  const isDisabled = typeof isEnable !== 'boolean';

  if (isDisabled) {
    return (
      <div className={st.NotificationCellBody}>
        <span className={st.Status}>{t('недоступно')}</span>
      </div>
    );
  }

  const renderTime = () => {
    if (!pausedNotification) return null;

    const diffMs = pausedNotification.time - Date.now();
    const minutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(diffMs / (1000 * 60 * 60));

    if (minutes < 60) {
      return (
        <TruncatedText
          className={st.TooltipWidth}
          hasTruncation
          tooltipContent={t('vlNotifier:time.minutes', { minutes })}
        >
          {t('vlNotifier:time.minutes', { minutes })}
        </TruncatedText>
      );
    }
    return (
      <TruncatedText
        className={st.TooltipWidth}
        hasTruncation
        tooltipContent={t('vlNotifier:time.hours', { hours })}
      >
        {t('vlNotifier:time.hours', { hours })}
      </TruncatedText>
    );
  };

  const renderStatus = () => {
    if (pausedNotification) {
      return (
        <div className={st.PauseTimeTitle}>
          <span className={cn(st.Status, st.Status_pause)}>{t('vlNotifier:pause')}</span>
          <span className={st.PausedTime}>{renderTime()}</span>
        </div>
      );
    }
    return (
      <span className={cn(st.Status, { [st.Status_active]: isEnable })}>
        {isEnable ? t('кнопка.включено') : t('кнопка.отключено')}
      </span>
    );
  };

  const renderButtons = () => {
    if (!isEnable && !pausedNotification) {
      return (
        <ButtonScrimGroup size="small">
          <Control.IconButton
            data-testid="onNotificationUIEnable"
            icon={<PlayCircleIcon />}
            kind="positive"
            onClick={onClick}
            variant="dimmed"
          />
        </ButtonScrimGroup>
      );
    }
    return (
      <ButtonScrimGroup size="small">
        {!pausedNotification && isEnable && notificationId !== 'create' && (
          <Control.IconButton
            data-testid="onPauseNotificationUIEnable"
            icon={<PauseCircleIcon />}
            kind="attention"
            onClick={() => onPause(notification, 'add', Date.now() + 60 * 60 * 1000)}
            variant="dimmed"
            withDivider
          />
        )}
        {pausedNotification && isEnable && notificationId !== 'create' && (
          <Control.IconButton
            data-testid="offPauseNotificationUIEnable"
            icon={<PlayCircleIcon />}
            kind="positive"
            onClick={() => onPause(notification, 'delete')}
            variant="dimmed"
            withDivider
          />
        )}
        <Control.IconButton
          data-testid="offNotificationUIEnable"
          icon={<StopCircleIcon />}
          kind="negative"
          onClick={onClick}
          variant="dimmed"
        />
      </ButtonScrimGroup>
    );
  };

  return (
    <div className={cn(st.NotificationCellBody, { [st.NotificationCellBody_enable]: !isDisabled })}>
      {renderStatus()}
      {renderButtons()}
    </div>
  );
}

NotificationStatus.propTypes = {
  value: PropTypes.shape({
    notification: PropTypes.object,
    pausedNotification: PropTypes.object,
    notificationId: PropTypes.string,
    isEnable: PropTypes.bool,
    onClick: PropTypes.func,
    onPause: PropTypes.func,
  }).isRequired,
};
